/**
 * Why is this site quiet? — the closed vocabulary (ADR-0024 liveness, ADR-0028 §4).
 *
 * A stale `lastSeenAt` is the least informative fact the platform holds. On 2026-08-03 three
 * unrelated causes produced an identical stale timestamp on real sites:
 *
 *   • the host 403'd the WordPress loopback, so wp-cron could never spawn,
 *   • cron had been disabled during unrelated troubleshooting, and
 *   • nobody had visited a low-traffic site (wp-cron is visitor-driven).
 *
 * None was a security event. Every one was indistinguishable from a compromised site that had
 * stopped reporting — which for a security platform is the worst ambiguity there is, because the
 * safe reading and the likely reading point in opposite directions. Answering it took SSH, WP-CLI
 * and three wrong theories.
 *
 * The connector already exposes an **unauthenticated** liveness route whose *error shape* answers
 * most of it. That matters more than it sounds: the probe must work when the credential itself is
 * the broken thing, so it cannot require one.
 */

/**
 * What the platform could determine, ordered from "nothing works" to "everything works".
 *
 * Closed on purpose: a diagnosis the console cannot render is worse than none, so a new state has
 * to be added here, to the renderer, and to the tests together.
 */
export const SITE_DIAGNOSES = [
  /** The probe never got an HTTP response — DNS, TLS, refused, timed out. The site itself is down. */
  'site_unreachable',
  /**
   * HTTP answered, but the connector's liveness route did not.
   *
   * Deliberately NOT read as "not installed". Verified against the live fleet 2026-08-03: a site
   * running the current connector and reporting normally still answered `rest_no_route`, because its
   * `guard-dog/v1` namespace was not exposed. A deactivated connector and a filtered REST API are
   * indistinguishable from outside, so this states the observation, not a cause.
   */
  'connector_absent',
  /** The connector is active and says it is NOT enrolled. Its stored connection was lost. */
  'connector_disconnected',
  /**
   * The connector is active AND believes it is connected — it answered the signature challenge.
   * So enrollment and code are fine and the REPORTING path is what is broken: the scheduler is not
   * running, or its requests are not reaching us. This is the state all three real incidents were in.
   */
  'reporting_blocked',
  /** An HTTP response we do not recognise. Deliberately distinct from a confident answer. */
  'inconclusive',
  /** The site is checking in normally; there is nothing to diagnose. */
  'reporting',
] as const;

export type SiteDiagnosis = (typeof SITE_DIAGNOSES)[number];

export interface SiteDiagnosisResult {
  diagnosis: SiteDiagnosis;
  /** When the probe ran. */
  checkedAt: string;
  /** The site's last check-in at probe time, so the console can show both facts together. */
  lastSeenAt?: string;
  /**
   * One short, safe line of evidence — an HTTP status, an error code the connector returned, or the
   * reason a probe was refused. Never a raw transport error, which can carry internal detail.
   */
  detail: string;
}

/** The connector's public liveness path (ADR-0024). Fixed here so no caller can supply a path. */
export const LIVENESS_PATH = '/wp-json/guard-dog/v1/liveness';

/**
 * How long a site may be silent before "quiet" is worth asking about.
 *
 * Deliberately generous. wp-cron is **visitor-driven**, so a quiet low-traffic site is entirely
 * normal — healthy production sites were observed more than an hour apart on 2026-08-03, and one was
 * misdiagnosed as broken for exactly that reason. Alerting at the 5-minute heartbeat cadence would
 * fire on every quiet site, and a signal that fires constantly is one nobody reads.
 */
export const QUIET_AFTER_MS = 90 * 60 * 1000;

/**
 * Is this site quiet enough to be worth diagnosing?
 *
 * Lives in `contracts` because the API decides whether to probe and the console decides whether to
 * offer the button, and two copies of this threshold would drift into disagreeing about which sites
 * are worth asking about — the console offering a check the API then declines to run.
 *
 * A site that has NEVER checked in counts as quiet: that is the most diagnosable state there is.
 */
export function isSiteQuiet(
  lastSeenAt: string | undefined | null,
  now: number = Date.now(),
): boolean {
  if (!lastSeenAt) return true;
  const seen = Date.parse(lastSeenAt);
  if (!Number.isFinite(seen)) return true;
  return now - seen > QUIET_AFTER_MS;
}
